import { useTranslation } from '@/hooks/use-translation';
import { ANALYTICS_EVENTS, analytics } from '@/services/analytics';
import type { paths } from '@/services/api-allo/api-allo-v1';
import { $apiAllo } from '@/services/api-allo/client';
import { $apiAlloRemote } from '@/services/api-allo/client-remote';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

type UserResponse = paths['/v1/user']['get']['responses'][200]['content']['application/json'];

type Mode = 'BUSY' | 'FREE';

type MutationContext = {
  previousData: UserResponse | undefined;
};

type UseUserBusyModeMutationOptions = {
  showSuccessToast?: boolean;
};

export const useUserBusyModeMutation = ({
  showSuccessToast = true,
}: UseUserBusyModeMutationOptions = {}) => {
  const { t } = useTranslation('desktop_ui');
  const queryClient = useQueryClient();

  const userQueryKey = $apiAllo.queryOptions('get', '/v1/user', {}).queryKey;

  return $apiAlloRemote.useMutation('patch', '/v1/user/config', {
    onMutate: async (variables): Promise<MutationContext> => {
      await queryClient.cancelQueries({ queryKey: userQueryKey });

      const previousData = queryClient.getQueryData<UserResponse>(userQueryKey);
      const nextMode = (variables.body as { mode?: Mode })?.mode;

      if (nextMode) {
        queryClient.setQueryData<UserResponse>(userQueryKey, (old) => {
          if (!old?.data) {
            return old;
          }
          return {
            ...old,
            data: {
              ...old.data,
              config: { ...old.data.config, mode: nextMode },
            },
          };
        });
      }

      return { previousData };
    },
    onSuccess: (_, variables) => {
      const isBusy = (variables.body as { mode?: Mode })?.mode === 'BUSY';
      analytics.track(
        isBusy ? ANALYTICS_EVENTS.BUSY_MODE.ENABLED : ANALYTICS_EVENTS.BUSY_MODE.DISABLED,
      );
      if (showSuccessToast) {
        toast.success(
          isBusy
            ? t('components.sidebar.menu.busyStatusEnabled', "You're now set as busy")
            : t('components.sidebar.menu.busyStatusDisabled', "You're now available"),
        );
      }
    },
    onError: (_error, _variables, context) => {
      const ctx = context as MutationContext | undefined;
      if (ctx?.previousData !== undefined) {
        queryClient.setQueryData(userQueryKey, ctx.previousData);
      }
      toast.error(
        t('components.sidebar.menu.busyStatusUpdateFailed', 'Failed to update your status'),
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userQueryKey });
    },
  });
};
